import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Phone, Mail, Building, TrendingUp, BarChart3, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { mockChatbotResponse, mockChatResponses } from './mock';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    projectDetails: ''
  });
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "👋 Welcome to Knowledge Ninja Lab! I'm your AI Research Assistant. I'll help you find the perfect solution for your business intelligence needs.",
      sender: 'bot',
      timestamp: new Date(),
      type: 'welcome'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const serviceOptions = [
    { 
      id: 'market-research', 
      label: 'Market Research & Business Intelligence', 
      icon: BarChart3,
      description: 'Competitive analysis and market insights'
    },
    { 
      id: 'mentorship', 
      label: 'Student Support & Mentorship', 
      icon: User,
      description: 'Academic guidance and presentation coaching'
    },
    { 
      id: 'presentations', 
      label: 'Business Cases & Presentations', 
      icon: FileText,
      description: 'Professional strategy decks and visual storytelling'
    },
    { 
      id: 'pitch-decks', 
      label: 'Startup Pitch Decks', 
      icon: TrendingUp,
      description: 'Investor-ready presentations'
    },
    { 
      id: 'consulting', 
      label: 'AI & Strategy Consulting', 
      icon: Bot,
      description: 'Strategic growth advisory and AI integration'
    }
  ];

  const quickActions = [
    { label: '📊 Market Analysis', action: 'market-research', icon: BarChart3 },
    { label: '🎯 Strategy Consulting', action: 'consulting', icon: TrendingUp },
    { label: '📈 Pitch Deck', action: 'pitch-decks', icon: FileText },
    { label: '💼 Get Quote', action: 'quote', icon: Building }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuickAction = (action) => {
    if (action === 'quote') {
      setCurrentStep('contact-info');
      addBotMessage("Great! I'd love to help you get a personalized quote. Let me gather some information about your project. What's your name?");
    } else {
      const service = serviceOptions.find(s => s.id === action);
      if (service) {
        setUserInfo(prev => ({ ...prev, service: service.label }));
        setCurrentStep('project-details');
        addBotMessage(`Excellent choice! ${service.label} is one of our core strengths. ${service.description}. Could you tell me more about your specific project requirements?`);
      }
    }
  };

  const addBotMessage = (text, type = 'response') => {
    const botMessage = {
      id: Date.now(),
      text,
      sender: 'bot',
      timestamp: new Date(),
      type
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const handleUserResponse = (response) => {
    const userMessage = {
      id: Date.now(),
      text: response,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    setIsTyping(true);
    setTimeout(() => {
      processUserResponse(response);
      setIsTyping(false);
    }, 1000);
  };

  const processUserResponse = (response) => {
    switch (currentStep) {
      case 'contact-info':
        if (!userInfo.name) {
          setUserInfo(prev => ({ ...prev, name: response }));
          addBotMessage(`Nice to meet you, ${response}! What's your email address?`);
        } else if (!userInfo.email) {
          setUserInfo(prev => ({ ...prev, email: response }));
          addBotMessage("Thank you! Could you provide your phone number?");
        } else if (!userInfo.phone) {
          setUserInfo(prev => ({ ...prev, phone: response }));
          addBotMessage("Perfect! What company or organization are you with?");
        } else if (!userInfo.company) {
          setUserInfo(prev => ({ ...prev, company: response }));
          setCurrentStep('service-selection');
          addBotMessage("Excellent! Now, which service are you most interested in?", 'service-options');
        }
        break;
      case 'project-details':
        setUserInfo(prev => ({ ...prev, projectDetails: response }));
        setCurrentStep('contact-info');
        addBotMessage("That sounds like a fascinating project! I'd like to connect you with our experts. What's your name?");
        break;
      case 'final':
        addBotMessage("Thank you for providing all the details! Our team will review your requirements and get back to you within 24 hours. You can also reach us directly at info.knowledgeninjalab@gmail.com or +91 9266849651.");
        setTimeout(() => {
          setCurrentStep('complete');
          addBotMessage("Is there anything else I can help you with today?");
        }, 2000);
        break;
      default:
        addBotMessage("I understand. How can I assist you further with your business intelligence needs?");
    }
  };

  const handleServiceSelection = (service) => {
    setUserInfo(prev => ({ ...prev, service: service.label }));
    setCurrentStep('final');
    handleUserResponse(service.label);
    
    setTimeout(() => {
      addBotMessage(`Perfect! Here's a summary of your inquiry:
      
📊 **Project Details:**
• Name: ${userInfo.name}
• Email: ${userInfo.email}
• Phone: ${userInfo.phone}
• Company: ${userInfo.company}
• Service: ${service.label}
${userInfo.projectDetails ? `• Requirements: ${userInfo.projectDetails}` : ''}

Our team will prepare a customized proposal for you!`);
    }, 1500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      handleUserResponse(inputMessage);
      setInputMessage('');
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const resetChat = () => {
    setCurrentStep('welcome');
    setUserInfo({
      name: '', email: '', phone: '', company: '', service: '', projectDetails: ''
    });
    setMessages([
      {
        id: 1,
        text: "👋 Welcome back! How can I help you with your business intelligence needs today?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'welcome'
      }
    ]);
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={() => setIsOpen(true)}
            className="w-16 h-16 rounded-full bg-gradient-to-r from-[#61525a] to-[#4a3f47] hover:from-[#4a3f47] hover:to-[#61525a] text-white shadow-2xl transition-all duration-300 transform hover:scale-110 group relative"
          >
            <Bot className="w-7 h-7 group-hover:scale-110 transition-transform duration-300" />
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-[#5f9dff] to-[#3dd3ee] rounded-full animate-pulse flex items-center justify-center">
              <span className="text-xs font-bold text-white">AI</span>
            </div>
          </Button>
          <div className="absolute -top-12 right-0 bg-[#1e1919] text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
            AI Research Assistant
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[28rem] h-[36rem] z-50 animate-in slide-in-from-bottom-4 duration-300">
          <Card className="h-full flex flex-col bg-white/95 backdrop-blur-sm border-0 shadow-2xl ring-1 ring-black/5">
            {/* Header */}
            <CardHeader className="bg-gradient-to-r from-[#1e1919] via-[#61525a] to-[#1e1919] text-white rounded-t-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#5f9dff] to-[#3dd3ee] rounded-full flex items-center justify-center relative">
                    <Bot className="w-6 h-6" />
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                  </div>
                  <div>
                    <CardTitle className="text-lg font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">AI Research Assistant</CardTitle>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <p className="text-xs text-white/90">Online • Powered by Knowledge Ninja Lab</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {currentStep !== 'welcome' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetChat}
                      className="text-white hover:bg-white/20 h-8 w-8 p-0"
                      title="Reset Chat"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 p-4 overflow-y-auto space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
              {messages.map((message) => (
                <div key={message.id}>
                  <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex max-w-[85%] ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.sender === 'user' 
                          ? 'bg-gradient-to-r from-[#5f9dff] to-[#3dd3ee] text-white ml-2 shadow-lg' 
                          : 'bg-gradient-to-r from-[#61525a] to-[#4a3f47] text-white mr-2 shadow-lg'
                      }`}>
                        {message.sender === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                          message.sender === 'user'
                            ? 'bg-gradient-to-r from-[#5f9dff] to-[#3dd3ee] text-white'
                            : 'bg-white text-gray-800 border border-gray-200'
                        }`}>
                          <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 px-2">
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Service Options */}
                  {message.type === 'service-options' && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs text-gray-500 px-2">Select a service:</p>
                      {serviceOptions.map((service) => {
                        const IconComponent = service.icon;
                        return (
                          <button
                            key={service.id}
                            onClick={() => handleServiceSelection(service)}
                            className="w-full p-3 bg-white rounded-lg border border-gray-200 hover:border-[#5f9dff] hover:bg-[#5f9dff]/5 transition-all duration-200 text-left group"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#5f9dff]/10 to-[#61525a]/10 flex items-center justify-center group-hover:from-[#5f9dff] group-hover:to-[#61525a] transition-all duration-200">
                                <IconComponent className="w-4 h-4 text-[#61525a] group-hover:text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-sm text-gray-800">{service.label}</p>
                                <p className="text-xs text-gray-500">{service.description}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-end space-x-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#61525a] to-[#4a3f47] text-white flex items-center justify-center shadow-lg">
                      <Bot className="w-4 h-4" />
                    </div>
                    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-200">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-[#5f9dff] rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-[#61525a] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-[#3dd3ee] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <span className="ml-2 text-xs text-gray-500">AI analyzing...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Quick Actions */}
            {messages.length === 1 && (
              <div className="p-4 bg-white border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-3 font-medium">🚀 Popular Services:</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions.map((action, index) => {
                    const IconComponent = action.icon;
                    return (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction(action.action)}
                        className="h-10 border-[#5f9dff]/30 text-[#5f9dff] hover:bg-gradient-to-r hover:from-[#5f9dff] hover:to-[#61525a] hover:text-white hover:border-transparent transition-all duration-200 group"
                      >
                        <IconComponent className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform duration-200" />
                        <span className="text-xs font-medium">{action.label}</span>
                      </Button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center justify-center space-x-2">
                  <div className="flex -space-x-1">
                    <div className="w-6 h-6 bg-gradient-to-r from-[#5f9dff] to-[#3dd3ee] rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-gradient-to-r from-[#61525a] to-[#4a3f47] rounded-full border-2 border-white"></div>
                    <div className="w-6 h-6 bg-gradient-to-r from-[#ff8c19] to-[#e67c15] rounded-full border-2 border-white"></div>
                  </div>
                  <p className="text-xs text-gray-400">Trusted by 100+ companies</p>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <form onSubmit={handleSubmit} className="flex space-x-3">
                <div className="flex-1 relative">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={
                      currentStep === 'contact-info' && !userInfo.name ? "Enter your name..." :
                      currentStep === 'contact-info' && !userInfo.email ? "Enter your email..." :
                      currentStep === 'contact-info' && !userInfo.phone ? "Enter your phone number..." :
                      currentStep === 'contact-info' && !userInfo.company ? "Enter your company name..." :
                      currentStep === 'project-details' ? "Describe your project requirements..." :
                      "Type your message..."
                    }
                    className="h-11 pl-4 pr-12 border-gray-200 focus:border-[#5f9dff] focus:ring-2 focus:ring-[#5f9dff]/20 rounded-xl"
                    disabled={isTyping}
                  />
                  {currentStep === 'contact-info' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {!userInfo.name ? <User className="w-4 h-4 text-gray-400" /> :
                       !userInfo.email ? <Mail className="w-4 h-4 text-gray-400" /> :
                       !userInfo.phone ? <Phone className="w-4 h-4 text-gray-400" /> :
                       <Building className="w-4 h-4 text-gray-400" />}
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!inputMessage.trim() || isTyping}
                  className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] hover:from-[#4a8ae8] hover:to-[#4a3f47] text-white h-11 w-11 p-0 rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              {currentStep === 'welcome' && (
                <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                  <span>🔒 Your data is secure</span>
                  <span>⚡ Instant responses</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export { ChatBot };